import React from "react";
import { CompletedByEffortSummary as CompletedByEffortSummaryType } from "../../../types";
import { formatDateForDisplay } from "../../../utils/dateUtils";
import { getCarEffortColor } from "../../../utils/appointmentUtils";
import { Loading, ErrorState } from "../../shared";
import { Icons } from "../../shared/Icons";

interface EmployeeCompletedByEffortSummaryProps {
  summary: CompletedByEffortSummaryType | null;
  loading: boolean;
  error: string | null;
  /** Inclusive range sent to the API (YYYY-MM-DD) */
  rangeStartDate: string;
  rangeEndDate: string;
}

function formatAvgMinutes(min: number | null): string {
  if (min == null) return "—";

  const totalMinutes = Math.round(min);
  if (totalMinutes < 60) return `${totalMinutes} min`;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (minutes > 0) return `${hours}h ${minutes}min`;
  return `${hours}h`;
}

export const EmployeeCompletedByEffortSummary: React.FC<EmployeeCompletedByEffortSummaryProps> = ({
  summary,
  loading,
  error,
  rangeStartDate,
  rangeEndDate,
}) => {
  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 p-3 sm:p-4 md:p-6">
        <h3 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-white mb-3">
          Resumo por esforço (veículos concluídos)
        </h3>
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 p-3 sm:p-4 md:p-6">
        <h3 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-white mb-3">
          Resumo por esforço (veículos concluídos)
        </h3>
        <ErrorState message={error} />
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 p-3 sm:p-4 md:p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div>
          <h3 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-white">
            Resumo por esforço (veículos concluídos)
          </h3>
          <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
            Período: {formatDateForDisplay(rangeStartDate)} — {formatDateForDisplay(rangeEndDate)}
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 px-4 py-3 shrink-0">
          <Icons.Car sx={{ fontSize: 28 }} className="text-slate-600 dark:text-slate-300" />
          <div>
            <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">Total concluídos com veículo</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">
              {summary.totalCompletedWithCar}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {(summary.byEffort ?? []).map((row) => (
          <div
            key={row.effort}
            className={`rounded-xl border p-4 ${getCarEffortColor(row.effort)}`}
          >
            <p className="text-xs font-semibold uppercase tracking-wide mb-2 opacity-80">
              Esforço {row.effort}
            </p>
            <div className="space-y-2">
              <div>
                <p className="text-xs opacity-80">Quantidade</p>
                <p className="text-xl font-bold tabular-nums">{row.completedCount}</p>
              </div>
              <div>
                <p className="text-xs opacity-80">Tempo médio</p>
                <p className="text-lg font-semibold tabular-nums">
                  {formatAvgMinutes(row.averageDurationMinutes)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
